/**
 * PluginRenderer — Renders a plugin arche in a sandboxed iframe.
 *
 * Responsibilities:
 * - Fetches plugin config from the arche record
 * - Creates sandboxed iframe with strict CSP
 * - Establishes postMessage bridge via PluginBridgeHost
 * - Handles resize, navigation, and toast events
 * - Shows loading/error states
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { API_BASE } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { toast } from '@/components/ui/use-toast';
import { PluginBridgeHost } from './PluginBridge';
import { PLUGIN_IFRAME_SANDBOX } from './pluginSandbox';
import { PurchaseConfirmationSheet } from './PurchaseConfirmationSheet';
import { ArcheLoadingScreen } from './ArcheLoadingScreen';
import { DARK_THEME, LIGHT_THEME, getThemeById, type PluginPermission } from '@fias/arche-sdk';
import { normalizeCdnBaseUrl, allVendoredLibraryAssets } from '@fias/platform-resources';

/**
 * Returns true if the bundleRef points to an S3-hosted bundle that should
 * be served through the API proxy (because the S3 bucket blocks public access).
 */
export function isS3BundleRef(bundleRef: string): boolean {
  return bundleRef.startsWith('s3://') || bundleRef.startsWith('plugin-bundles/');
}

/**
 * Resolve an `open_arche` bridge request host-side: navigate the top window to
 * another arche's page (`/a/<archeId>`). `newTab` is best-effort — the parent's
 * `window.open` can be popup-blocked because user activation does not cross the
 * iframe→host postMessage boundary, so we fall back to same-tab navigation when
 * the popup is blocked (returns null) and the click never silently no-ops.
 *
 * `targetArcheId` is already format-validated by the bridge before this runs.
 * `windowOpen` is injectable for testing. Exported for unit tests.
 */
export function openArcheFromHost(
  navigate: (path: string) => void,
  targetArcheId: string,
  newTab: boolean,
  windowOpen: (url: string, target: string, features: string) => Window | null = (url, t, f) =>
    window.open(url, t, f),
): void {
  const path = `/a/${targetArcheId}`;
  if (newTab) {
    const opened = windowOpen(path, '_blank', 'noopener,noreferrer');
    if (!opened) navigate(path);
  } else {
    navigate(path);
  }
}

interface PluginRendererProps {
  archeId: string;
}

/** Matches PluginConfig from @fias/db-types (redefined for client use) */
interface PluginConfig {
  bundleRef: string;
  manifestVersion: string;
  // Full client-safe permission union from the SDK (lockstep with db-types) —
  // see the note in PluginBridge.ts. Avoids the prior hand-listed subset that
  // omitted most scopes.
  permissions: PluginPermission[];
  sdkVersion: string;
  bundleSizeBytes: number;
  buildHash: string;
  themeId?: string;
  fontPairingId?: string;
  customTheme?: import('@fias/arche-sdk').FiasTheme;
}

/**
 * Returns true if the bundleRef should be fetched through the API proxy.
 * This includes:
 * - Source tarballs (local dev auto-approve, e.g. "user/plugin-sources/.../name.tar.gz")
 * - S3 keys (new format, e.g. "plugin-bundles/arc_xxx/v1/index.html")
 * - S3 URIs (old format, e.g. "s3://bucket/plugin-bundles/...")
 *
 * The S3 bucket blocks public access, so the browser cannot load S3 URLs
 * directly. All bundles must be proxied through the API endpoint.
 */
function shouldFetchViaApi(bundleRef: string): boolean {
  // Only external HTTPS URLs (non-S3) can be loaded directly by the browser
  if (bundleRef.startsWith('https://') && !bundleRef.includes('.s3.amazonaws.com/')) {
    return false;
  }
  return true;
}

export function PluginRenderer({ archeId }: PluginRendererProps) {
  const [pluginConfig, setPluginConfig] = useState<PluginConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const [srcdoc, setSrcdoc] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  // When the plugin's SDK announces a protocol version below what the
  // host requires, we capture the version pair and render an "update
  // needed" affordance instead of leaving a misbehaving iframe mounted.
  const [incompatibleSdk, setIncompatibleSdk] = useState<{
    sdkVersion: number;
    minRequired: number;
  } | null>(null);

  // Purchase confirmation state
  const [purchaseProduct, setPurchaseProduct] = useState<{
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  } | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const purchaseResolveRef = useRef<((value: unknown) => void) | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme: appTheme } = useTheme();
  const themeMode = appTheme === 'light' ? 'light' : 'dark';

  // Fetch arche data to get plugin config
  useEffect(() => {
    let mounted = true;

    async function loadPlugin() {
      try {
        const result = (await api.getArche(archeId)) as Record<string, unknown>;
        const config = result.pluginConfig as PluginConfig | null;

        if (!config) {
          throw new Error('This arche does not have a valid plugin configuration');
        }

        // Fetch bundles through the API proxy (S3 bucket blocks public access).
        // credentials: 'include' is required so the fias_session cookie flows
        // to the API on production (frontend and API live on different
        // subdomains; default fetch drops cookies on cross-origin). Without
        // it, signed-in users hit 401 on non-public plugins because
        // optionalAuth sees no token. Same pattern as api.ts:181.
        if (shouldFetchViaApi(config.bundleRef)) {
          const resp = await fetch(`${API_BASE}/arches/${archeId}/plugin-bundle`, {
            credentials: 'include',
          });
          if (!resp.ok) {
            const body = await resp.json().catch(() => null);
            const msg =
              (body as Record<string, Record<string, string>> | null)?.error?.message ||
              `Failed to load plugin bundle (${resp.status})`;
            throw new Error(msg);
          }
          const html = await resp.text();
          // The bundle was built with `__FIAS_API_BASE__` placeholders in
          // `<base href>` and the CSP `img-src` / `media-src` / `font-src`
          // allowlists. The placeholder is mis-named historically — it's
          // only used for plugin-asset URLs (the bridge call goes via
          // postMessage; the iframe CSP is `connect-src 'none'`).
          //
          // Prefer the dedicated plugin-assets CDN when configured —
          // CloudFront caches the immutable `/v1/arches/<id>/plugin-
          // assets/<version>/<file>` paths so repeated viewers don't hit
          // the API tier for each asset. Falls back to the API origin
          // when the env var is empty (local dev + envs before the CDN
          // stack deploys). Either origin serves the same path
          // (CloudFront forwards uncached requests to the same route),
          // so changing the substitution origin is a strict caching
          // optimization with no semantic difference.
          const pluginAssetsCdnBase = import.meta.env.VITE_PLUGIN_ASSETS_CDN_BASE as
            | string
            | undefined;
          const pluginAssetsOrigin =
            pluginAssetsCdnBase && pluginAssetsCdnBase.length > 0
              ? pluginAssetsCdnBase.replace(/\/$/, '')
              : new URL(API_BASE, window.location.origin).origin;
          const substituted = html.split('__FIAS_API_BASE__').join(pluginAssetsOrigin);
          if (mounted) {
            setSrcdoc(substituted);
            setPluginConfig(config);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setPluginConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load plugin');
          setLoading(false);
        }
      }
    }

    loadPlugin();
    return () => {
      mounted = false;
    };
  }, [archeId]);

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginConfig) return;

    setIframeReady(true);

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    bridgeRef.current = new PluginBridgeHost({
      archId: archeId,
      permissions: pluginConfig.permissions,
      iframe: iframeRef.current,
      onResize: (height) => setIframeHeight(height),
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
      },
      onNavigate: (path) => {
        navigate(path);
      },
      onOpenArche: (targetArcheId, newTab) => openArcheFromHost(navigate, targetArcheId, newTab),
      getTheme: () => {
        if (pluginConfig.customTheme) return pluginConfig.customTheme;
        if (pluginConfig.themeId) {
          const entry = getThemeById(pluginConfig.themeId);
          if (entry) return entry.theme;
        }
        return themeMode === 'dark' ? DARK_THEME : LIGHT_THEME;
      },
      // Self-hosted vendored-library asset URLs (only consulted for
      // `sandbox:vendored-libraries` plugins). Resolved from the client's
      // platform-resources CDN env; empty when unconfigured (local dev / pre-CDN
      // staging) → plugin degrades. The full registry is injected; the CSP
      // (widened per declared dep at build time) is what gates actual loading.
      getVendoredAssets: () =>
        allVendoredLibraryAssets(
          normalizeCdnBaseUrl(
            import.meta.env.VITE_PLATFORM_RESOURCES_CDN_DOMAIN as string | undefined,
          ),
        ),
      getUser: async () => ({
        userId: user?.userId ?? '',
        displayName: user?.username ?? 'User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      // Plugin iframes have an opaque origin and cannot send the
      // parent's session cookie. They authenticate to the bridge with
      // a per-arche capability JWT (audience=plugin-bridge, 5min TTL,
      // archeId-scoped). The token has NO authority outside the
      // /v1/plugins/bridge/:archId route — a leak cannot drain credits
      // or read vault data via any other endpoint.
      getAuthToken: async () => (await authApi.getPluginBridgeToken(archeId)) ?? '',
      onIncompatibleSdk: (sdkVersion, minRequired) => {
        setIncompatibleSdk({ sdkVersion, minRequired });
      },
      onPurchaseRequest: (product) => {
        return new Promise((resolve) => {
          // Fetch user balance for the confirmation sheet
          api
            .get<{ balance: number }>('/credits/balance')
            .then((res) => setUserBalance(res.data?.balance ?? null))
            .catch(() => setUserBalance(null));
          setPurchaseProduct(product);
          purchaseResolveRef.current = resolve;
        });
      },
    });

    bridgeRef.current.init();
  }, [archeId, pluginConfig, navigate, user, themeMode]);

  // Send theme update when mode changes
  useEffect(() => {
    bridgeRef.current?.sendThemeUpdate();
  }, [themeMode]);

  // Clean up bridge and pending purchase on unmount
  useEffect(() => {
    return () => {
      if (purchaseResolveRef.current) {
        purchaseResolveRef.current({ success: false, error: 'USER_CANCELED' });
        purchaseResolveRef.current = null;
      }
      bridgeRef.current?.destroy();
    };
  }, []);

  const handlePurchaseConfirm = useCallback(async () => {
    if (!purchaseProduct || !bridgeRef.current) return;
    setPurchaseLoading(true);
    try {
      // Same-origin call — use cookie auth (the api wrapper handles
      // credentials:'include' + CSRF). No bearer token round-trip
      // needed; this isn't running inside the sandboxed iframe.
      const { data: result } = await api.post<unknown>(`/plugins/bridge/${archeId}`, {
        type: 'store_purchase',
        payload: { productIdentifier: purchaseProduct.productIdentifier },
      });
      purchaseResolveRef.current?.(result as { success: boolean; error?: string });
    } catch {
      purchaseResolveRef.current?.({ success: false, error: 'Purchase failed' });
    } finally {
      setPurchaseLoading(false);
      setPurchaseProduct(null);
      purchaseResolveRef.current = null;
    }
  }, [purchaseProduct, archeId]);

  const handlePurchaseCancel = useCallback(() => {
    purchaseResolveRef.current?.({ success: false, error: 'USER_CANCELED' });
    setPurchaseProduct(null);
    purchaseResolveRef.current = null;
  }, []);

  if (loading) {
    return <ArcheLoadingScreen />;
  }

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Plugin Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pluginConfig) return null;

  if (incompatibleSdk) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin was built against an older version of the Fias SDK (v
              {incompatibleSdk.sdkVersion}) than the platform currently supports (v
              {incompatibleSdk.minRequired}+). The plugin author needs to rebuild with an updated
              SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const iframeProps = srcdoc ? { srcDoc: srcdoc } : { src: pluginConfig.bundleRef };

  return (
    <div className="relative w-full h-full min-h-0">
      <iframe
        ref={iframeRef}
        {...iframeProps}
        onLoad={handleIframeLoad}
        sandbox={PLUGIN_IFRAME_SANDBOX}
        referrerPolicy="no-referrer"
        className="w-full border-0 block"
        style={{
          height: iframeHeight != null ? `${iframeHeight}px` : '100%',
          background: 'hsl(var(--background))',
          visibility: iframeReady ? 'visible' : 'hidden',
        }}
        title={`Plugin: ${archeId}`}
      />
      {!iframeReady && (
        <div className="absolute inset-0">
          <ArcheLoadingScreen />
        </div>
      )}
      <PurchaseConfirmationSheet
        open={purchaseProduct !== null}
        product={purchaseProduct}
        userBalance={userBalance}
        loading={purchaseLoading}
        onConfirm={handlePurchaseConfirm}
        onCancel={handlePurchaseCancel}
      />
    </div>
  );
}
